import { useEffect, useMemo, useState, type ReactNode } from 'react';
import axios from 'axios';
import { Box, Button, CircularProgress, Dialog, Paper, Typography } from '@mui/material';

import TimezoneSelect from '@/components/shared/TimezoneSelect';
import { useAuth } from '@/hooks/useAuth';
import {
  getWeeklyAvailabilitySchedule,
  initializeWeeklyAvailabilitySchedule,
  type WeeklyAvailabilitySchedule,
} from '@/lib/api/availability';
import { updateStoredUserProfile } from '@/lib/api/auth';

interface AvailabilityOnboardingGateProps {
  children: ReactNode;
}

type AvailabilityCheckResult =
  | { status: 'ready'; schedule: WeeklyAvailabilitySchedule }
  | { status: 'timezone-required' };

const availabilityChecks = new Map<string, Promise<AvailabilityCheckResult>>();

function readApiErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) return '';
  const data = error.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    return String(record.message ?? record.error ?? '');
  }
  return '';
}

function isTimezoneRequiredError(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 400 &&
    readApiErrorMessage(error)
      .toLowerCase()
      .includes('timezone is required before initializing availability')
  );
}

function getInitialTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

function markAvailabilityInitialized(): void {
  // Timezone is intentionally omitted from local auth storage, so only cache the
  // initialization flag that the Appointments gate needs on later visits.
  updateStoredUserProfile({ availabilityInitialized: true });
}

function checkAndInitializeAvailability(userKey: string): Promise<AvailabilityCheckResult> {
  const existingCheck = availabilityChecks.get(userKey);
  if (existingCheck) return existingCheck;

  const check = (async (): Promise<AvailabilityCheckResult> => {
    const schedule = await getWeeklyAvailabilitySchedule();
    if (schedule.availabilityInitialized === true) {
      return { status: 'ready', schedule };
    }

    // Rely on the backend profile timezone when present. Local auth storage strips
    // timezone, so the frontend must not invent one here.
    try {
      const initializedSchedule = await initializeWeeklyAvailabilitySchedule();
      return { status: 'ready', schedule: initializedSchedule };
    } catch (error) {
      if (isTimezoneRequiredError(error)) return { status: 'timezone-required' };
      throw error;
    }
  })();

  availabilityChecks.set(userKey, check);
  void check
    .finally(() => {
      if (availabilityChecks.get(userKey) === check) {
        availabilityChecks.delete(userKey);
      }
    })
    .catch(() => undefined);
  return check;
}

const AvailabilityOnboardingGate = ({ children }: AvailabilityOnboardingGateProps) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<'loading' | 'ready' | 'timezone-required' | 'error'>(
    user?.availabilityInitialized === true ? 'ready' : 'loading',
  );
  const [selectedTimezone, setSelectedTimezone] = useState(getInitialTimezone);
  const [isInitializing, setIsInitializing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  const userKey = useMemo(
    () => String(user?.id ?? user?.email ?? 'authenticated-nutritionist'),
    [user?.email, user?.id],
  );

  useEffect(() => {
    if (user?.availabilityInitialized === true) {
      setStatus('ready');
      return;
    }

    let cancelled = false;
    setStatus('loading');
    setErrorMessage('');

    void checkAndInitializeAvailability(userKey)
      .then((result) => {
        if (cancelled) return;
        if (result.status === 'timezone-required') {
          setStatus('timezone-required');
          return;
        }

        markAvailabilityInitialized();
        setStatus('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        setErrorMessage(
          readApiErrorMessage(error) || 'We could not prepare your availability. Please try again.',
        );
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [retryCount, user?.availabilityInitialized, userKey]);

  const initializeWithSelectedTimezone = async () => {
    if (!selectedTimezone || isInitializing) return;
    setIsInitializing(true);
    setErrorMessage('');

    try {
      await initializeWeeklyAvailabilitySchedule({
        timezone: selectedTimezone,
      });
      markAvailabilityInitialized();
      setStatus('ready');
    } catch (error) {
      if (isTimezoneRequiredError(error)) {
        setErrorMessage('Select a timezone before continuing.');
      } else {
        setErrorMessage(
          readApiErrorMessage(error) || 'We could not initialize your availability. Please try again.',
        );
      }
    } finally {
      setIsInitializing(false);
    }
  };

  if (status === 'ready') return <>{children}</>;

  if (status === 'error') {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center', p: 3 }}>
        <Paper
          elevation={0}
          sx={{ maxWidth: 520, width: '100%', p: { xs: 2.5, sm: 4 }, textAlign: 'center', border: '1px solid #E4E7EC', borderRadius: 3 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1D2939' }}>
            Availability could not be loaded
          </Typography>
          <Typography sx={{ mt: 1, color: '#667085' }}>{errorMessage}</Typography>
          <Button
            variant="contained"
            onClick={() => setRetryCount((count) => count + 1)}
            sx={{ mt: 3, textTransform: 'none' }}
          >
            Try again
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Box sx={{ display: 'grid', justifyItems: 'center', gap: 1.25 }}>
          <CircularProgress size={30} />
          <Typography sx={{ color: '#667085' }}>Preparing your appointments...</Typography>
        </Box>
      </Box>

      <Dialog
        open={status === 'timezone-required'}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1D2939' }}>
            Choose your timezone
          </Typography>
          <Typography sx={{ mt: 1, color: '#667085' }}>
            We use your timezone to create your initial Monday–Friday availability from 9:00 AM
            to 4:00 PM.
          </Typography>
          <Box sx={{ mt: 3 }}>
            <TimezoneSelect
              value={selectedTimezone}
              onChange={setSelectedTimezone}
              placeholder="Search for your timezone..."
            />
          </Box>
          {errorMessage ? (
            <Typography role="alert" sx={{ mt: 1.5, color: '#B42318', fontSize: 14 }}>
              {errorMessage}
            </Typography>
          ) : null}
          <Button
            fullWidth
            variant="contained"
            disabled={!selectedTimezone || isInitializing}
            onClick={() => void initializeWithSelectedTimezone()}
            sx={{ mt: 3, minHeight: 44, textTransform: 'none', fontWeight: 600 }}
          >
            {isInitializing ? 'Saving...' : 'Continue'}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default AvailabilityOnboardingGate;
